import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { Icon } from '@whalar/ui/src/components/Icon/Icon';
import { Paragraph, TextLink } from 'ui-components';

import {
  ContentDetailMetricsContentType,
  ContentDetailMetricsItemType,
} from '../ContentDetailMetrics.types';

export interface PopoverGlossaryProps<
  C extends ContentDetailMetricsContentType,
> {
  contentData: C;
  metrics: ContentDetailMetricsItemType<C>;
  onlyIcon?: boolean;
}

export function PopoverGlossary<C extends ContentDetailMetricsContentType>({
  metrics,
  contentData,
  onlyIcon = false,
}: PopoverGlossaryProps<C>) {
  const renderMetric = ([
    metricKey,
    { title, description, metrics, hideInPopover },
  ]: [
    string,
    {
      title?: string | ((contentData: C) => string);
      description?: string;
      metrics?: any;
      hideInPopover?: boolean;
    },
  ]): any => {
    if (hideInPopover) return null;
    return description ? (
      <Box key={metricKey}>
        <Box marginTop={'spacing-75'}>
          <Paragraph size="sm" fontWeight="medium">
            {typeof title === 'function' ? title(contentData) : title}
          </Paragraph>
          <Paragraph as="span" marginTop={0} size="sm" fontWeight="normal">
            {description}
          </Paragraph>
        </Box>

        {metrics && Object.entries(metrics).map(renderMetric)}
      </Box>
    ) : null;
  };
  const hasDescriptions: boolean = Object.values(metrics).some(
    ({ description }) => !!description,
  );
  return hasDescriptions ? (
    <Popover placement="top">
      <PopoverTrigger>
        {onlyIcon ? (
          <TextLink>
            <Icon name="Info" className="text-16 text-neutral-300" />
          </TextLink>
        ) : (
          <TextLink>Learn more about metrics</TextLink>
        )}
      </PopoverTrigger>
      <PopoverContent
        maxW={'100vw'}
        w="auto"
        h={'auto'}
        borderColor={'primary.100'}
      >
        <PopoverCloseButton marginTop={2} />
        <PopoverBody padding={'spacing-200'}>
          <Flex direction={'column'}>
            <Paragraph size="md" fontWeight="medium">
              Metrics data
            </Paragraph>
            {Object.entries(metrics).map(renderMetric)}
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  ) : (
    <Box />
  );
}
